import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { featureCollection, point } from "@turf/helpers";
import { useHistory } from "react-router-dom";
import { MappingContext } from "@app/mapping/MappingContext";
import { ZipProjectContext } from "@app/projects/zip-projects/ZipProjectContext";
import { NetworkAssessmentAnalysisType, ProjectionProviderType, ProjectRunConfigurationType } from "@app/projects/enumerators";
import { useZipCoverageMapData } from "@app/projects/zip-projects/maps/useZipCoverageMapData";
import { useZipCoverageServiceAreas } from "@app/projects/zip-projects/maps/useZipCoverageServiceAreas";
import { useZipCoverageZipMembership } from "@app/projects/zip-projects/maps/useZipCoverageZipMembership";
import {
  applyZeroMembershipFilter,
  isZeroMembershipSelection,
} from "@app/projects/zip-projects/maps/zipCoverageServiceAreaFilter";
import { ZipCoverageMap } from "@app/projects/zip-projects/maps/ZipCoverageMap";
import { ZipCoverageMapDetailsBox } from "@app/projects/zip-projects/maps/ZipCoverageMapDetailsBox";
import { ZipCoverageMapFilterPanel } from "@app/projects/zip-projects/maps/ZipCoverageMapFilterPanel";
import { ZipCoverageMapProviderPanel } from "@app/projects/zip-projects/maps/ZipCoverageMapProviderPanel";
import { ZipCoverageMapProviderPopup } from "@app/projects/zip-projects/maps/ZipCoverageMapProviderPopup";
import { ZipCoverageMapFloatingButtons } from "@app/projects/zip-projects/maps/ZipCoverageMapFloatingButtons";
import { useZipCoverageMapDetails } from "@app/projects/zip-projects/maps/useZipCoverageMapDetails";
import { buildZipCoverageMapDetails } from "@app/projects/zip-projects/maps/zipCoverageMapDetails";
import {
  ZipCoverageMapMembershipType,
  ZipCoverageMapSelections,
  ZipCoverageResultsAsOfOption,
  ZipProviderFilters,
} from "@app/projects/zip-projects/maps/interfaces";
import {
  buildZipProviderFilterOptionTree,
  DEFAULT_ZIP_PROVIDER_FILTERS,
} from "@app/projects/zip-projects/maps/zipProviderFilters";
import { canViewTerminatedProviders } from "@app/projects/terminationRoleGate";
import { UserContext } from "@app/user/UserContext";
import { DropdownItem, NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { ProviderMapResults } from "@app/projects/results/common/mapping";
import { outOfNetworkProviderNetworkTypes, ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { formatDateCustom } from "@app/shared/formatDateTime";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { ZipCoverageMapExport } from "@app/projects/zip-projects/maps/ZipCoverageMapExport";
import { buildMultiZipServiceArea, isMultiZipSelection } from "@app/projects/zip-projects/maps/multiZipSelection";
import { getMembershipTypeLabel } from "@app/projects/membershipTypeLabel";
import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import { getPrimaryAndSecondaryRunConfigurations } from "@app/projects/network-assessment/adequacy/utility";
import { NetworkIntegrityMembership } from "@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants";
import { getLastAppliedMembership } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityContext";
import { getLastVisitedZipModule } from "@app/projects/zip-projects/state-management/lastVisitedZipModule";
import type { LngLatBounds } from "mapbox-gl";

const EMPTY_SPECIALTY: DropdownItem<string> = { key: "", name: "" };
const DEFAULT_PROVIDER_SOURCES: ProviderNetworkTypeEnum[] = [ProviderNetworkTypeEnum.Network];

const stripSpecialtyCodePrefix = (option: DropdownItem<string>): DropdownItem<string> => {
  const codePrefix = `${option.key} - `;
  return {
    key: option.key,
    name: option.name.startsWith(codePrefix) ? option.name.slice(codePrefix.length) : option.name,
  };
};

/**
 * Route container for the ZIP coverage map (counterpart of county's NetworkCoverageMap.tsx).
 * Consumes ZipProjectContext and owns the map-setup selection state (draft vs applied).
 * Delivers the setup inputs + Create/Clear All; map generation is a separate story.
 */
export const ZipCoverageMapRoute: FC = () => {
  const { state, onProcessProviderChanges, onUpdateUnprocessedProjectionProviders } = useContext(ZipProjectContext);
  const mappingContext = useContext(MappingContext);
  const enabledFeatureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const userContext = useContext(UserContext);
  const history = useHistory();

  // Excluding projected/termed/dual-supplemental, newest first.
  const resultsAsOfOptions = useMemo<ZipCoverageResultsAsOfOption[]>(
    () =>
      (state.project?.projectRunConfigurations ?? [])
        .filter(
          (rc) =>
            !rc.isProjected &&
            !rc.isTermed &&
            !rc.isProjectedTermed &&
            rc.projectRunConfigurationType !== ProjectRunConfigurationType.DualMembershipSupplemental,
        )
        .slice()
        .sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime())
        .map((rc) => ({
          id: rc.projectRunConfigurationId,
          label: rc.dateCreated ? (!rc.datePublished ? "Unpublished " : "") + formatDateCustom(rc.dateCreated, "MMM D, 'YY hh:mmA") : "Unknown Date",
        })),
    [state.project],
  );

  const defaultResultsAsOfId = useMemo<number | null>(() => {
    const current = state.runConfiguration?.projectRunConfigurationId;
    if (current && resultsAsOfOptions.some((option) => option.id === current)) {
      return current;
    }

    if (state.runConfiguration?.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental) {
      const companionId = state.runConfiguration.companionProjectRunConfigurationId;
      if (companionId && resultsAsOfOptions.some((option) => option.id === companionId)) {
        return companionId;
      }
    }
    return resultsAsOfOptions[0]?.id ?? null;
  }, [resultsAsOfOptions, state.runConfiguration]);

  // Draft = what's picked in the setup panel
  // Applied = what the map has rendered(set on Create).
  // Effective = draft, falling back to the default until the user picks one.
  const [draftResultsAsOfId, setDraftResultsAsOfId] = useState<number | null>(null);
  const effectiveResultsAsOfId = draftResultsAsOfId ?? defaultResultsAsOfId;

  // Keyed on the drafted Results As Of: each run configuration has its own service area.
  const serviceAreas = useZipCoverageServiceAreas(
    state.project?.projectId ?? null,
    effectiveResultsAsOfId,
  );

  const { primaryRunConfiguration, secondaryRunConfiguration } = useMemo(() => {
    const configs = state.project?.projectRunConfigurations ?? [];
    if (effectiveResultsAsOfId === null || !configs.some((rc) => rc.projectRunConfigurationId === effectiveResultsAsOfId)) {
      return { primaryRunConfiguration: undefined, secondaryRunConfiguration: undefined };
    }
    return getPrimaryAndSecondaryRunConfigurations(configs, effectiveResultsAsOfId);
  }, [state.project, effectiveResultsAsOfId]);

  const showMembershipTypeFilter = !!secondaryRunConfiguration;

  const [draftMembershipType, setDraftMembershipType] = useState<ZipCoverageMapMembershipType>(ZipCoverageMapMembershipType.Primary);

  // No companion for the newly-selected timeslice: fall back to Primary
  useEffect(() => {
    if (!secondaryRunConfiguration && draftMembershipType === ZipCoverageMapMembershipType.Secondary) {
      setDraftMembershipType(ZipCoverageMapMembershipType.Primary);
    }
  }, [secondaryRunConfiguration, draftMembershipType]);

  // Like county: Network always; Prospect/Market under "Out of Network" only when the run
  // configuration exposes those provider set ids.
  const providerSourceItems = useMemo<NestingParentDropdownItem[]>(() => {
    const items: NestingParentDropdownItem[] = [
      {
        checkboxStatus: CheckboxStateEnum.Unchecked,
        children: [],
        key: ProviderNetworkTypeEnum.Network,
        name: "Network Servicing Providers",
      },
    ];
    // Gated on ZIP_PROSPECT_AND_MARKET as well as the provider-set ids, matching the zip Providers tab.
    // With the flag off the map is Network-only, which also makes Targeted/Excluded pins unreachable —
    // those statuses only ever ride on Prospect/Market providers.
    const zipProspectAndMarketEnabled = enabledFeatureFlags.has(Features.ZIP_PROSPECT_AND_MARKET);
    const outOfNetwork: DropdownItem<string>[] = [];
    if (zipProspectAndMarketEnabled && state.runConfiguration?.prospectProviderSetId) {
      outOfNetwork.push({ key: ProviderNetworkTypeEnum.Prospect, name: "Prospect Providers" });
    }
    if (zipProspectAndMarketEnabled && state.runConfiguration?.marketProviderSetId) {
      outOfNetwork.push({ key: ProviderNetworkTypeEnum.Market, name: "Market Providers" });
    }
    if (outOfNetwork.length > 0) {
      items.push({
        checkboxStatus: CheckboxStateEnum.Unchecked,
        children: outOfNetwork,
        key: "out-of-network",
        name: "Out of Network Providers",
      });
    }
    return items;
  }, [enabledFeatureFlags, state.runConfiguration]);

  // Drop the "All Specialty Groups" sentinel (key "all"); the map needs a concrete specialty.
  // Match county: strip the "CODE - " prefix, sort by description, drop the "all" sentinel.
  const specialtyOptions = useMemo<DropdownItem<string>[]>(
    () =>
      (state.filterOptions?.specialtyGroupCode ?? [])
        .filter((option) => option.key !== "all")
        .map(stripSpecialtyCodePrefix)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [state.filterOptions],
  );

  // Draft = what's picked in the setup panel; applied = what the map has rendered (set on Create).
  const allowMultipleServiceAreas = enabledFeatureFlags.has(Features.SERVICE_AREA_WIDE_MAPPING_ZIP);
  const [draftServiceAreaKeys, setDraftServiceAreaKeys] = useState<string[]>([]);
  const [draftSpecialty, setDraftSpecialty] = useState<DropdownItem<string>>(EMPTY_SPECIALTY);
  const [draftProviderSources, setDraftProviderSources] =
    useState<ProviderNetworkTypeEnum[]>(DEFAULT_PROVIDER_SOURCES);
  const [appliedSelections, setAppliedSelections] = useState<ZipCoverageMapSelections | null>(null);

  // The run configuration the map will actually read — Secondary swaps in the companion time slice. Hoisted out of
  // createMap so the zero-membership classification below can't come from a different timeslice than the map.
  const membershipRunConfigurationId = useMemo<number | null>(
    () =>
      draftMembershipType === ZipCoverageMapMembershipType.Secondary && secondaryRunConfiguration
        ? secondaryRunConfiguration.projectRunConfigurationId
        : effectiveResultsAsOfId,
    [draftMembershipType, secondaryRunConfiguration, effectiveResultsAsOfId],
  );

  // 210362: the Service Area tree is geography, so it lists zips the analysis found no members in. Off by default,
  // those are hidden (deliberately unlike the Adequacy tab, which shows them).
  const [showZeroMembership, setShowZeroMembership] = useState(false);
  const { zipCodesWithMembership, hasMembershipData, isLoading: loadingZipMembership } = useZipCoverageZipMembership(
    state.project?.projectId ?? null,
    membershipRunConfigurationId,
  );

  // Until membership answers — or if the call fails — the whole tree stands, rather than filtering everything away
  // and leaving the user nothing to select.
  const serviceAreaItems = useMemo(
    () => (hasMembershipData
      ? applyZeroMembershipFilter(serviceAreas.items, serviceAreas.selectionLookup, zipCodesWithMembership, showZeroMembership)
      : serviceAreas.items),
    [hasMembershipData, serviceAreas.items, serviceAreas.selectionLookup, zipCodesWithMembership, showZeroMembership],
  );

  // One filter object for both the setup panel and the Filters flyout, so they can't disagree.
  const [providerFilters, setProviderFilters] = useState<ZipProviderFilters>(DEFAULT_ZIP_PROVIDER_FILTERS);
  const [selectedProvider, setSelectedProvider] = useState<ProviderMapResults | null>(null);

  useEffect(() => {
    setSelectedProvider(null);
  }, [appliedSelections]);

  const [mapStyle, setMapStyle] = useState<string>(mappingContext.qesOutdoorsStyle);
  const [showMembersWithAccess, setShowMembersWithAccess] = useState(true);
  const [showMembersWithoutAccess, setShowMembersWithoutAccess] = useState(true);
  const [showZipBoundary, setShowZipBoundary] = useState(true);
  const [showZipPoints, setShowZipPoints] = useState(false);
  const [showCounty, setShowCounty] = useState(false);

  // Provider Listing panel. Lifted here (not panel-local) because both ZipCoverageMap
  // (viewport reporting) and ZipCoverageMapProviderPanel (viewport filtering, collapse) need them;
  const [mapBounds, setMapBounds] = useState<LngLatBounds | null>(null);
  const [isProviderPanelOpen, setIsProviderPanelOpen] = useState(true);

  const hasPrefilledFromAdequacy = useRef(false);
  // The zip carried over from Adequacy, remembered until either the membership check below has had a look at it or
  // the user takes the selection over (see handleSelectServiceAreas / clearAll).
  const prefilledServiceAreaKey = useRef<string | null>(null);
  useEffect(() => {
    if (hasPrefilledFromAdequacy.current || serviceAreas.isLoading) {
      return;
    }
    hasPrefilledFromAdequacy.current = true;

    const { zipCode, specialtyGroupCode } = state.filterSelections;

    if (zipCode) {
      const match = [...serviceAreas.selectionLookup.entries()].find(
        ([, area]) => area.zipCode === zipCode,
      );
      if (match) {
        setDraftServiceAreaKeys([match[0]]);
        prefilledServiceAreaKey.current = match[0];
      }
    }

    if (specialtyGroupCode?.key && specialtyGroupCode.key !== "all") {
      setDraftSpecialty(stripSpecialtyCodePrefix(specialtyGroupCode));
    }

    const isSecondaryOnAdequacy = state.runConfiguration?.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental;
    const lastAppliedNiMembership = getLastAppliedMembership();
    const preferNi = getLastVisitedZipModule() === "NetworkIntegrity" && lastAppliedNiMembership !== undefined;
    setDraftMembershipType(
      preferNi
        ? (lastAppliedNiMembership === NetworkIntegrityMembership.Secondary ? ZipCoverageMapMembershipType.Secondary : ZipCoverageMapMembershipType.Primary)
        : (isSecondaryOnAdequacy ? ZipCoverageMapMembershipType.Secondary : ZipCoverageMapMembershipType.Primary),
    );
  }, [serviceAreas.isLoading, serviceAreas.selectionLookup, state.filterSelections, state.runConfiguration]);

  const selectedServiceAreas = useMemo(
    () => draftServiceAreaKeys
      .map((key) => serviceAreas.selectionLookup.get(key))
      .filter((area) => area !== undefined),
    [draftServiceAreaKeys, serviceAreas.selectionLookup],
  );
  const selectedServiceArea = selectedServiceAreas[0] ?? null;

  // A key that no longer resolves is outside the newly-picked run configuration's service area. Nothing
  // is cleared until the tree has answered, so a slow fetch can't wipe a valid selection.
  useEffect(() => {
    if (serviceAreas.isLoading || !serviceAreas.hasData) {
      return;
    }
    if (draftServiceAreaKeys.every((key) => serviceAreas.selectionLookup.has(key))) {
      return;
    }
    prefilledServiceAreaKey.current = null;
    setDraftServiceAreaKeys((keys) => keys.filter((key) => serviceAreas.selectionLookup.has(key)));
    setAppliedSelections(null);
  }, [serviceAreas.isLoading, serviceAreas.hasData, serviceAreas.selectionLookup, draftServiceAreaKeys]);

  // A zip the toggle hides can't stay selected (AC 7). This also catches switching Results As Of or membership type
  // into a timeslice where the selected zip has no members. Nothing is cleared until membership has actually
  // answered, so a slow or failed fetch can't wipe the selection.
  useEffect(() => {
    if (showZeroMembership || loadingZipMembership || !hasMembershipData) {
      return;
    }
    const zeroMembershipKeys = draftServiceAreaKeys.filter((key) =>
      isZeroMembershipSelection(key, serviceAreas.selectionLookup, zipCodesWithMembership));
    if (zeroMembershipKeys.length === 0) {
      return;
    }
    if (prefilledServiceAreaKey.current !== null && zeroMembershipKeys.includes(prefilledServiceAreaKey.current)) {
      // Deep-linked from Adequacy, which lists zero-membership zips by default. Reveal the carried-over selection
      // rather than silently dropping it.
      prefilledServiceAreaKey.current = null;
      setShowZeroMembership(true);
      return;
    }
    setDraftServiceAreaKeys((keys) => keys.filter((key) => !zeroMembershipKeys.includes(key)));
  }, [showZeroMembership, loadingZipMembership, hasMembershipData, zipCodesWithMembership, draftServiceAreaKeys, serviceAreas.selectionLookup]);

  // County-wide boundaries feed the label layer; the focused zip's own boundary (fetched
  // separately, keyed off appliedSelections.serviceArea.zipCode) feeds the outline layer + framing.
  const {
    boundaries, loadingBoundaries, focusedZipBoundary, loadingFocusedZipBoundary,
    countyBoundaries, loadingCountyBoundaries,
    memberFeatures,
    providerFeatures, providers, filteredProviders, loadingProviders, providerFetchErrors, refreshProviders,
    hasExcludedProviders,
    visibleHasTargetedProviders, visibleHasTerminatedProviders, visibleHasExcludedProviders,
  } = useZipCoverageMapData(
    appliedSelections?.serviceArea.countyRegionSetId ?? null,
    appliedSelections?.serviceArea.zipCode ?? null,
    state.project?.projectId ?? null,
    appliedSelections,
    providerFilters,
  );

  const framingProviderFeatures = useMemo(
    () => featureCollection(providers.map((p) => point([p.longitude, p.latitude], p))),
    [providers],
  );

  // The action-status pin colors are keyed to the run configuration the MAP applied, which can differ from the
  // project's currently-selected one. Resolved explicitly rather than defaulting open the way county's
  // `!datePublished` fallback does.
  const appliedRunConfiguration = useMemo(
    () => (state.project?.projectRunConfigurations ?? [])
      .find((rc) => rc.projectRunConfigurationId === appliedSelections?.resultsAsOfId) ?? null,
    [appliedSelections, state.project],
  );
  const isLatestPublishedRunConfiguration = appliedRunConfiguration?.isLatestPublished === true;

  const membershipChipLabel = enabledFeatureFlags.has(Features.DUAL_MEMBERSHIP_FOR_MAPS)
    ? getMembershipTypeLabel(appliedRunConfiguration)
    : null;

  const canViewTerminations = canViewTerminatedProviders(userContext?.roles);
  const providerFilterOptions = useMemo(
    () => buildZipProviderFilterOptionTree(providers, { canViewTerminations, isLatestPublished: isLatestPublishedRunConfiguration }),
    [providers, canViewTerminations, isLatestPublishedRunConfiguration],
  );

  // Re-resolve against the latest fetch so a popup action shows up without re-clicking the pin.
  useEffect(() => {
    if (!selectedProvider) {
      return;
    }
    const fresh = filteredProviders.find((p) => p.providerUniqueId === selectedProvider.providerUniqueId);
    if (fresh !== selectedProvider) {
      setSelectedProvider(fresh ?? null);
    }
  }, [filteredProviders, selectedProvider]);

  // Keyed off appliedSelections (the Create snapshot) so draft filter changes never move the box.
  const { row: detailsRow, footnote: detailsFootnote, classRow: detailsClassRow } = useZipCoverageMapDetails(state.project?.projectId ?? null, appliedSelections);
  const details = useMemo(
    () => (appliedSelections ? buildZipCoverageMapDetails(detailsRow, detailsFootnote, appliedSelections, state.calculationMethod) : null),
    [detailsRow, detailsFootnote, appliedSelections, state.calculationMethod],
  );

  // Out-of-Network tab visibility (202347): keys off which sources were actually applied at Create,
  // not a second flag check — Prospect/Market are only ever in providerSources when
  // ZIP_PROSPECT_AND_MARKET is on AND the run configuration exposes the matching provider set id
  // (see providerSourceItems above), so re-deriving the flag here would just duplicate that gate.
  const showOutOfNetworkTab = (appliedSelections?.providerSources ?? []).some((source) =>
    outOfNetworkProviderNetworkTypes.includes(source));
  const showNetworkTab = (appliedSelections?.providerSources ?? []).includes(ProviderNetworkTypeEnum.Network);
  const passPercentage = details?.passPercentage ?? null;

  const canTargetProviders = enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TARGETING);

  const handleTargetSubmitted = (runProjection: boolean, hasUnprocessedProviders = true): void => {
    if (runProjection) {
      onProcessProviderChanges(false);
    } else {
      onUpdateUnprocessedProjectionProviders(
        hasUnprocessedProviders || !!state.project?.hasUnprocessedTargetProviders,
        ProjectionProviderType.Target,
      );
    }
    refreshProviders();
  };

  const handleTargetRemoved = (runProjection: boolean): void => {
    if (runProjection) {
      onProcessProviderChanges(false);
    } else {
      onUpdateUnprocessedProjectionProviders(true, ProjectionProviderType.Target);
    }
    refreshProviders();
  };

  if (!state.project || !state.runConfiguration) {
    return <LoadingSpinner />;
  }

  // Distance/time tile visibility — county's gate: run-config flag AND project option.
  const showAverageDistance = state.runConfiguration.showDistance && !!state.project.showAverageDistance;
  const showMaximumDistance = state.runConfiguration.showDistance && !!state.project.showMaximumDistance;
  const showAverageTime = state.runConfiguration.showTime && !!state.project.showAverageTime;
  const showMaximumTime = state.runConfiguration.showTime && !!state.project.showMaximumTime;

  // Both buttons share county's gate: enabled only when Results As Of + Service Area + Specialty +
  // >=1 Provider Source are all selected. (County also requires a county status; out of scope for ZIP.
  // Advanced Filters never affect enablement.)
  const mapButtonsDisabled =
    effectiveResultsAsOfId === null ||
    selectedServiceArea === null ||
    !draftSpecialty.key ||
    draftProviderSources.length === 0;
  const createDisabled = loadingBoundaries || loadingFocusedZipBoundary || loadingCountyBoundaries || mapButtonsDisabled;
  const clearAllDisabled = mapButtonsDisabled;

  const stripCarriedOverAdequacyQueryString = (): void => {
    if (history.location.search) {
      history.replace(history.location.pathname);
    }
  };

  const handleSelectServiceAreas = (keys: string[]): void => {
    stripCarriedOverAdequacyQueryString();
    // Once the user picks (or clears) a zip themselves, nothing is "carried over from Adequacy" any more, so the
    // reveal-instead-of-clear exemption below has to retire with it.
    prefilledServiceAreaKey.current = null;
    setDraftServiceAreaKeys(keys);
  };

  const handleSelectSpecialty = (specialty: DropdownItem<string>): void => {
    stripCarriedOverAdequacyQueryString();
    setDraftSpecialty(specialty);
  };

  // Layer menu defaults, shared by Clear All and Update Inputs so the two can't drift — the county map
  // returns the layers to defaults on both paths.
  const resetLayerSelections = (): void => {
    setMapStyle(mappingContext.qesOutdoorsStyle);
    setShowMembersWithAccess(true);
    setShowMembersWithoutAccess(true);
    setShowZipBoundary(true);
    setShowZipPoints(false);
    setShowCounty(false);
  };

  const clearAll = (): void => {
    stripCarriedOverAdequacyQueryString();
    prefilledServiceAreaKey.current = null;
    setDraftResultsAsOfId(null);
    setDraftMembershipType(ZipCoverageMapMembershipType.Primary);
    setDraftServiceAreaKeys([]);
    setDraftSpecialty(EMPTY_SPECIALTY);
    setDraftProviderSources(DEFAULT_PROVIDER_SOURCES);
    setShowZeroMembership(false);
    setAppliedSelections(null);
    setProviderFilters(DEFAULT_ZIP_PROVIDER_FILTERS);
    resetLayerSelections();
    setMapBounds(null);
    setSelectedProvider(null);
  };

  const handleServesRangeChange = (range: [number, number]): void =>
    setProviderFilters((filters) => ({ ...filters, servesRange: range }));

  const handleGainsRangeChange = (range: [number, number]): void =>
    setProviderFilters((filters) => ({ ...filters, gainsRange: range }));

  const handleImpactRangeChange = (range: [number, number]): void =>
    setProviderFilters((filters) => ({ ...filters, impactRange: range }));

  const handleShowZipBoundaryChange = (checked: boolean): void => {
    setShowZipBoundary(checked);
    if (!checked) setShowZipPoints(false);
  };

  const createMap = (): void => {
    // membershipRunConfigurationId is null only when effectiveResultsAsOfId is; the Secondary branch always resolves.
    if (membershipRunConfigurationId === null || !selectedServiceArea) {
      return;
    }

    setAppliedSelections({
      membershipType: draftMembershipType,
      providerSources: draftProviderSources,
      resultsAsOfId: membershipRunConfigurationId,
      serviceArea: selectedServiceAreas.length > 1 ? buildMultiZipServiceArea(selectedServiceAreas) : selectedServiceArea,
      serviceAreas: selectedServiceAreas,
      specialtyGroupCode: draftSpecialty,
    });
  };

  // "Update Inputs" clears the rendered map and reopens the setup panel
  const updateInputs = (): void => {
    setAppliedSelections(null);
    resetLayerSelections();
    setMapBounds(null);
    setSelectedProvider(null);
    // The next selection fetches different providers, so drop the source/status options; keep the measures.
    setProviderFilters((filters) => ({ ...filters, providerFilterOptions: [] }));
  };

  const showSelectionPanel = appliedSelections === null;
  const isMultiZip = isMultiZipSelection(appliedSelections);

  return (
    <div className="mt-3 zip-coverage-map">
      {!showSelectionPanel && (<ZipCoverageMapExport
        appliedSelections={appliedSelections}
        isLoading={loadingProviders}
        membershipType={getMembershipTypeLabel(appliedRunConfiguration)}
        projectName={state.project.resource.name}
        resultsAsOfDate={appliedRunConfiguration?.dateCreated ?? null}
      />)}
      {!isMultiZip && (<ZipCoverageMapDetailsBox
        details={details}
        appliedSelections={appliedSelections}
        appliedProviderFilters={providerFilters}
        membershipLabel={membershipChipLabel}
        showAverageDistance={showAverageDistance}
        showMaximumDistance={showMaximumDistance}
        showAverageTime={showAverageTime}
        showMaximumTime={showMaximumTime}
      />)}
      <ZipCoverageMap
        boundaries={boundaries}
        focusedZipBoundary={focusedZipBoundary}
        countyBoundaries={countyBoundaries}
        memberFeatures={memberFeatures}
        providerFeatures={providerFeatures}
        framingProviderFeatures={framingProviderFeatures}
        selectedProvider={selectedProvider}
        onProviderSelect={setSelectedProvider}
        providerPopup={
          selectedProvider && appliedSelections && appliedRunConfiguration ? (
            <ZipCoverageMapProviderPopup
              provider={selectedProvider}
              onClose={() => setSelectedProvider(null)}
              showProviderActionStatus={isLatestPublishedRunConfiguration}
              runConfiguration={{...appliedRunConfiguration, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType.ServiceArea} as ProjectRunConfigurationResult}
              specialtyGroupCode={appliedSelections.specialtyGroupCode.key}
              regionSetId={detailsRow?.regionSetId ?? null}
              project={state.project}
              canTargetProviders={canTargetProviders}
              zipBaselineRow={detailsRow}
              zipClassBaselineRow={detailsClassRow}
              onTargetSubmitted={handleTargetSubmitted}
              onTargetRemoved={handleTargetRemoved}
            />
          ) : undefined
        }
        isLatestPublishedRunConfiguration={isLatestPublishedRunConfiguration}
        selectedProviderSources={appliedSelections?.providerSources ?? []}
        hasTargetedProviders={visibleHasTargetedProviders}
        hasTerminatedProviders={visibleHasTerminatedProviders}
        hasExcludedProviders={visibleHasExcludedProviders}
        mapStyle={mapStyle}
        onMapStyleChange={setMapStyle}
        showLayerControl={!showSelectionPanel}
        showMembersWithAccess={showMembersWithAccess}
        onShowMembersWithAccessChange={setShowMembersWithAccess}
        showMembersWithoutAccess={showMembersWithoutAccess}
        onShowMembersWithoutAccessChange={setShowMembersWithoutAccess}
        showZipBoundary={showZipBoundary}
        onShowZipBoundaryChange={handleShowZipBoundaryChange}
        showZipPoints={showZipPoints}
        alwaysLabelZipPoints={isMultiZip}
        onShowZipPointsChange={setShowZipPoints}
        showCounty={showCounty}
        onShowCountyChange={setShowCounty}
        onMapBoundsChange={setMapBounds}
        leftPanel={
          showSelectionPanel ? (
            <ZipCoverageMapFilterPanel
              resultsAsOfOptions={resultsAsOfOptions}
              selectedResultsAsOfId={effectiveResultsAsOfId}
              onSelectResultsAsOf={setDraftResultsAsOfId}
              showMembershipTypeFilter={showMembershipTypeFilter}
              membershipType={draftMembershipType}
              onSelectMembershipType={setDraftMembershipType}
              primaryMembershipAlias={primaryRunConfiguration?.memberSetAlias}
              secondaryMembershipAlias={secondaryRunConfiguration?.memberSetAlias}
              serviceAreaItems={serviceAreaItems}
              serviceAreaLoading={serviceAreas.isLoading || loadingZipMembership}
              selectedServiceAreaKeys={draftServiceAreaKeys}
              selectedServiceAreaLabel={selectedServiceArea?.label ?? ""}
              onSelectServiceAreas={handleSelectServiceAreas}
              allowMultipleServiceAreas={allowMultipleServiceAreas}
              showZeroMembership={showZeroMembership}
              onShowZeroMembershipChange={setShowZeroMembership}
              specialtyOptions={specialtyOptions}
              selectedSpecialty={draftSpecialty}
              onSelectSpecialty={handleSelectSpecialty}
              providerSourceItems={providerSourceItems}
              selectedProviderSources={draftProviderSources}
              onSelectProviderSources={setDraftProviderSources}
              servesRange={providerFilters.servesRange}
              onServesRangeChange={handleServesRangeChange}
              gainsRange={providerFilters.gainsRange}
              onGainsRangeChange={handleGainsRangeChange}
              impactRange={providerFilters.impactRange}
              onImpactRangeChange={handleImpactRangeChange}
              createDisabled={createDisabled}
              clearAllDisabled={clearAllDisabled}
              onCreateMap={createMap}
              onClearAll={clearAll}
              isLoading={loadingBoundaries || loadingCountyBoundaries}
            />
          ) : (
            <ZipCoverageMapProviderPanel
              providerFeatures={providerFeatures}
              totalProviders={providers}
              loadingProviders={loadingProviders}
              providerFetchErrors={providerFetchErrors}
              mapBounds={mapBounds}
              passPercentage={passPercentage}
              showNetworkTab={showNetworkTab}
              showOutOfNetworkTab={showOutOfNetworkTab}
              selectedProvider={selectedProvider}
              onSelectProvider={setSelectedProvider}
              isOpen={isProviderPanelOpen}
              onToggleOpen={() => setIsProviderPanelOpen((open) => !open)}
            />
          )
        }
        mapOverlay={
          !showSelectionPanel && (
            <ZipCoverageMapFloatingButtons
              appliedRegionLabel={appliedSelections?.serviceArea.label ?? ""}
              appliedSpecialtyLabel={
                appliedSelections?.specialtyGroupCode.key
                  ? appliedSelections.specialtyGroupCode.name
                  : undefined
              }
              isLeftPanelOpen={isProviderPanelOpen}
              onUpdateInputs={updateInputs}
              providerFilterOptions={providerFilterOptions}
              appliedFilters={providerFilters}
              onApplyFilters={setProviderFilters}
              hasExcludedProviders={hasExcludedProviders}
              loadingProviders={loadingProviders}
            />
          )
        }
      />
    </div>
  );
};

export default ZipCoverageMapRoute;
